import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useFormik } from 'formik';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import * as Yup from 'yup';
import {
  ACCOUNT_STATUS,
  AMOUNT_INPUT_VALUE_TYPE,
  COMMON_CURRENCY_PRECISION,
  CRYPTO_CURRENCY_PRECISION,
  EXCHANGE_AMOUNT_DIRECTION,
  SEARCH_BOUND_MINIMUM_LENGTH_ACCOUNT
} from 'components/constants';
import CustomAsyncSelect from 'components/CustomAsyncSelect';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { getCustomersListRequest } from 'services/requestAgent';
import { formatMoney, isCryptoProvider } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import { ReactComponent as CurrencyExchangeSwitchIcon } from 'assets/switch-icon.svg';
import AmountInput from 'uikit/AmountInput';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import { IconButton } from 'uikit/IconButton/IconButton';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import timerColors from 'uikit/stylesheets/variables.scss';
import './ExchangeTransaction.scss';

const CALCULATE_DEBOUNCE_MS = 500;

const AMOUNT_FIELD_BY_DIRECTION = {
  [EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM]: 'fromAmount',
  [EXCHANGE_AMOUNT_DIRECTION.AMOUNT_TO]: 'toAmount'
};

const toCustomerLabel = (account) => `${account.account_holder}, ${account.account_number}`;

const toCustomerOption = (customer) => {
  const account = {
    account_number: customer.account_number,
    account_holder: customer.common_name,
    account_type: customer.type
  };

  return { value: account, label: toCustomerLabel(account) };
};

const toWalletLabel = (wallet) => `${wallet.currency} — ${wallet.wallet_number}`;

// Two wallets can't be used on opposite sides of an exchange if they'd exchange the same currency
// into itself — except crypto wallets of the same currency on different networks.
const areAccountsConflicting = (account1, account2) => {
  if (!account1 || !account2) return false;

  const isCrypto1 = isCryptoProvider(account1.transfer_provider);
  const isCrypto2 = isCryptoProvider(account2.transfer_provider);

  if (isCrypto1 && isCrypto2) {
    return account1.currency === account2.currency && account1.network === account2.network;
  }

  return account1.currency === account2.currency;
};

const findAccountForExchange = (accounts, excludeAccount) =>
  accounts.find(
    (account) =>
      account.wallet_number !== excludeAccount?.wallet_number && !areAccountsConflicting(account, excludeAccount)
  );

const ExchangeTransaction = ({ customersStore, exchangeTransactionsStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const [asyncCustomerLabelValue, setAsyncCustomerLabelValue] = useState('');
  const [lastEditedField, setLastEditedField] = useState(EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM);
  const calculateTimeoutRef = useRef(null);

  const form = useFormik({
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      customerAccount: null,
      walletFrom: null,
      walletTo: null,
      fromAmount: '',
      toAmount: ''
    },
    validationSchema: Yup.object({
      customerAccount: Yup.object().nullable().required(i18n.getMessage('createTransaction.validation.isRequired')),
      walletFrom: Yup.object().nullable().required(i18n.getMessage('createTransaction.validation.isRequired')),
      walletTo: Yup.object()
        .nullable()
        .required(i18n.getMessage('createTransaction.validation.isRequired'))
        .test('different-wallet', i18n.getMessage('createTransaction.exchange.error.sameWallet'), function (value) {
          return !value || !this.parent.walletFrom || value.wallet_number !== this.parent.walletFrom.wallet_number;
        }),
      fromAmount: Yup.number()
        .typeError(i18n.getMessage('createTransaction.validation.isRequired'))
        .positive(i18n.getMessage('createTransaction.validation.mustBePositive'))
        .required(i18n.getMessage('createTransaction.validation.isRequired'))
    }),
    onSubmit: async (formValues) => {
      const quote = exchangeTransactionsStore.quote;
      const calculation = exchangeTransactionsStore.calculation;
      const isReadyToSubmit =
        !!quote &&
        !!calculation &&
        Number(calculation.fromAmount) > 0 &&
        !calculation.amountExceeded &&
        !exchangeTransactionsStore.isLoading;

      if (!isReadyToSubmit) return;

      await exchangeTransactionsStore.createExchange(
        formValues.customerAccount.account_number,
        quote.quoteId,
        calculation.fromAmount
      );
    }
  });

  const { values, errors, handleSubmit, setFieldValue, resetForm, validateField } = form;

  useEffect(() => {
    return () => {
      exchangeTransactionsStore.resetExchangeTransactionsStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (exchangeTransactionsStore.isNewExchangeTransactionCreated) {
      resetForm();
      setAsyncCustomerLabelValue('');
      exchangeTransactionsStore.setIsNewExchangeTransactionCreated(false);
      navigate(`${ROUTE_PATHS.TRANSACTIONS}/${exchangeTransactionsStore.newTransactionId}`);
      exchangeTransactionsStore.resetQuote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeTransactionsStore.isNewExchangeTransactionCreated]);

  // Fetch a new quote as soon as both wallets are selected and differ
  useEffect(() => {
    if (
      values.customerAccount &&
      values.walletFrom &&
      values.walletTo &&
      values.walletFrom.wallet_number !== values.walletTo.wallet_number
    ) {
      exchangeTransactionsStore.createQuote(
        values.customerAccount.account_number,
        values.walletFrom.wallet_number,
        values.walletTo.wallet_number
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.walletFrom?.wallet_number, values.walletTo?.wallet_number]);

  // Mirror the calculate response into the amount field the operator isn't currently editing
  useEffect(() => {
    const calculation = exchangeTransactionsStore.calculation;
    if (!calculation) return;

    if (lastEditedField === EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM) {
      setFieldValue('toAmount', calculation.toAmount != null ? String(calculation.toAmount) : '');
    } else {
      setFieldValue('fromAmount', calculation.fromAmount != null ? String(calculation.fromAmount) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeTransactionsStore.calculation]);

  // Whenever a fresh quote lands (initial pick, wallet swap, or the countdown auto-refresh) and the
  // operator already had a valid amount typed, recalculate against it instead of leaving it stale.
  useEffect(() => {
    const quote = exchangeTransactionsStore.quote;
    if (!quote || !values.customerAccount) return;

    const amount = Number(values[AMOUNT_FIELD_BY_DIRECTION[lastEditedField]]);
    if (amount > 0) {
      exchangeTransactionsStore.calculate(
        values.customerAccount.account_number,
        quote.quoteId,
        amount,
        lastEditedField
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeTransactionsStore.quote]);

  const activeWallets = (customersStore.currentCustomerDetails?.wallets || []).filter(
    (wallet) => wallet.status === ACCOUNT_STATUS.ACTIVE
  );

  const walletFromOptions = activeWallets
    .filter((wallet) => !areAccountsConflicting(wallet, values.walletTo))
    .map((wallet) => ({ key: wallet.wallet_number, value: toWalletLabel(wallet) }));

  const walletToOptions = activeWallets
    .filter((wallet) => !areAccountsConflicting(wallet, values.walletFrom))
    .map((wallet) => ({ key: wallet.wallet_number, value: toWalletLabel(wallet) }));

  const getAmountPrecision = (wallet) =>
    wallet && isCryptoProvider(wallet.transfer_provider) ? CRYPTO_CURRENCY_PRECISION : COMMON_CURRENCY_PRECISION;

  const getAvailableBalanceLabel = (wallet) => {
    if (!wallet) return undefined;

    return i18n.getMessage('createTransaction.exchange.wallet.availableBalance', {
      amount: formatMoney(wallet.available, getAmountPrecision(wallet)),
      currency: wallet.currency
    });
  };

  const scheduleCalculate = (amount, direction) => {
    setLastEditedField(direction);

    if (calculateTimeoutRef.current) {
      clearTimeout(calculateTimeoutRef.current);
    }

    calculateTimeoutRef.current = setTimeout(() => {
      const quote = exchangeTransactionsStore.quote;
      const numericAmount = Number(amount);

      if (quote && numericAmount > 0 && values.customerAccount) {
        exchangeTransactionsStore.calculate(
          values.customerAccount.account_number,
          quote.quoteId,
          numericAmount,
          direction
        );
      }
    }, CALCULATE_DEBOUNCE_MS);
  };

  const handleChangeCustomerOption = (option) => {
    const account = option?.value;

    if (!account) return;

    setFieldValue('customerAccount', { account_number: account.account_number, account_type: account.account_type });
    setFieldValue('walletFrom', null);
    setFieldValue('walletTo', null);
    setFieldValue('fromAmount', '');
    setFieldValue('toAmount', '');
    setAsyncCustomerLabelValue(toCustomerLabel(account));
    exchangeTransactionsStore.resetQuote();
    customersStore.getCustomerDetailsById(account.account_number, account.account_type);
  };

  const handleChangeWalletFrom = (name, walletNumber) => {
    const wallet = activeWallets.find((item) => item.wallet_number === walletNumber) || null;
    const walletTo = areAccountsConflicting(wallet, values.walletTo)
      ? findAccountForExchange(activeWallets, wallet)
      : values.walletTo;
    setFieldValue('walletFrom', wallet);
    setFieldValue('walletTo', walletTo || null);
    setFieldValue('fromAmount', '');
    setFieldValue('toAmount', '');
    exchangeTransactionsStore.resetQuote();
  };

  const handleChangeWalletTo = (name, walletNumber) => {
    const wallet = activeWallets.find((item) => item.wallet_number === walletNumber) || null;
    const walletFrom = areAccountsConflicting(wallet, values.walletFrom)
      ? findAccountForExchange(activeWallets, wallet)
      : values.walletFrom;
    setFieldValue('walletTo', wallet);
    setFieldValue('walletFrom', walletFrom || null);
    setFieldValue('fromAmount', '');
    setFieldValue('toAmount', '');
    exchangeTransactionsStore.resetQuote();
  };

  const handleSwitchWallets = () => {
    setFieldValue('walletFrom', values.walletTo);
    setFieldValue('walletTo', values.walletFrom);
  };

  const handleFromAmountChange = (e) => {
    const value = e.target.value;
    setFieldValue('fromAmount', value);
    validateField('fromAmount');
    scheduleCalculate(value, EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM);
  };

  const handleToAmountChange = (e) => {
    const value = e.target.value;
    setFieldValue('toAmount', value);
    scheduleCalculate(value, EXCHANGE_AMOUNT_DIRECTION.AMOUNT_TO);
  };

  const handleRefreshQuote = () => {
    if (!values.customerAccount || !values.walletFrom || !values.walletTo) return;

    setFieldValue('fromAmount', '');
    setFieldValue('toAmount', '');
    exchangeTransactionsStore.createQuote(
      values.customerAccount.account_number,
      values.walletFrom.wallet_number,
      values.walletTo.wallet_number
    );
  };

  // Runs automatically when the countdown reaches 0. Unlike handleRefreshQuote (used by the manual
  // retry button after a failed fetch), it keeps whatever amount the operator already typed — the
  // quote-watching effect above recalculates against it once the fresh quote lands.
  const handleAutoRefreshQuote = async () => {
    if (!values.customerAccount || !values.walletFrom || !values.walletTo) return;

    await exchangeTransactionsStore.createQuote(
      values.customerAccount.account_number,
      values.walletFrom.wallet_number,
      values.walletTo.wallet_number
    );
  };

  const customerLoadOptions = async (searchText) => {
    let response = null;
    if (searchText?.length >= SEARCH_BOUND_MINIMUM_LENGTH_ACCOUNT) {
      response = await getCustomersListRequest('account_number', 0, 10, 'ASC', searchText, {});
    }

    return response ? response.content.map(toCustomerOption) : [];
  };

  const noOptionsMessage = () => i18n.getMessage('createTransaction.payment.accountSelect.noAccountsFound');
  const loadingMessage = ({ inputValue }) =>
    inputValue.trim()?.length < SEARCH_BOUND_MINIMUM_LENGTH_ACCOUNT
      ? i18n.getMessage('createTransaction.payment.accountSelect.searchWillStartWith') +
        SEARCH_BOUND_MINIMUM_LENGTH_ACCOUNT +
        i18n.getMessage('createTransaction.payment.accountSelect.characters')
      : i18n.getMessage('createTransaction.payment.accountSelect.searching');

  const quote = exchangeTransactionsStore.quote;
  const calculation = exchangeTransactionsStore.calculation;

  const canSubmit =
    !!values.customerAccount &&
    !!values.walletFrom &&
    !!values.walletTo &&
    !!quote &&
    !!calculation &&
    Number(calculation?.fromAmount) > 0 &&
    !calculation?.amountExceeded &&
    !exchangeTransactionsStore.isLoading &&
    !exchangeTransactionsStore.isLoadingCalculate;

  // A failed quote fetch leaves the operator without a usable rate — offer a manual retry.
  // Other failures (e.g. calculate/create) still show the same alert but keep the current quote intact.
  const isQuoteRetryAvailable = !!exchangeTransactionsStore.error && !quote && !!values.walletFrom && !!values.walletTo;

  return (
    <div className={'exchange-transaction-wrapper'}>
      <form className={'exchange-transaction-form'} onSubmit={handleSubmit}>
        <div className={'exchange-transaction-row-block'}>
          <CustomAsyncSelect
            isRequired
            className={'exchange-transaction-account-async-select'}
            onChange={handleChangeCustomerOption}
            loadList={customerLoadOptions}
            handleInputChange={() => {}}
            name={'customerAccount'}
            noOptionsMessage={noOptionsMessage}
            loadingMessage={loadingMessage}
            label={i18n.getMessage('createTransaction.exchange.customerAccount.label')}
            placeholder={i18n.getMessage('createTransaction.exchange.customerAccount.placeholder')}
            error={errors?.customerAccount}
            valueLabel={asyncCustomerLabelValue}
          />
        </div>

        <div className={'exchange-transaction-row-block'}>
          <InputDropDown
            isRequired
            className={'exchange-transaction-select'}
            label={i18n.getMessage('createTransaction.exchange.walletFrom.label')}
            placeholder={i18n.getMessage('createTransaction.exchange.wallet.placeholder')}
            topLabel={getAvailableBalanceLabel(values.walletFrom)}
            name={'walletFrom'}
            value={values.walletFrom?.wallet_number}
            options={walletFromOptions}
            onChange={handleChangeWalletFrom}
            isDisabled={!values.customerAccount || customersStore.isLoading}
            error={errors?.walletFrom}
          />
          <IconButton
            className={'exchange-transaction-switch-button'}
            Icon={CurrencyExchangeSwitchIcon}
            onClick={handleSwitchWallets}
            isDisabled={(!values.walletFrom && !values.walletTo) || exchangeTransactionsStore.isLoadingQuote}
          />
          <InputDropDown
            isRequired
            className={'exchange-transaction-select'}
            label={i18n.getMessage('createTransaction.exchange.walletTo.label')}
            placeholder={i18n.getMessage('createTransaction.exchange.wallet.placeholder')}
            topLabel={getAvailableBalanceLabel(values.walletTo)}
            name={'walletTo'}
            value={values.walletTo?.wallet_number}
            options={walletToOptions}
            onChange={handleChangeWalletTo}
            isDisabled={!values.customerAccount || customersStore.isLoading}
            error={errors?.walletTo}
          />
        </div>

        {exchangeTransactionsStore.isLoadingQuote && !quote && (
          <div className={'exchange-transaction-quote-loader'}>
            <Loader />
          </div>
        )}

        {exchangeTransactionsStore.error && (
          <div className={'exchange-transaction-row-block'}>
            <Alert
              type={'warning'}
              message={
                exchangeTransactionsStore.error?.message || i18n.getMessage('createTransaction.exchange.error.generic')
              }
            />
            {isQuoteRetryAvailable && (
              <Button type={'tertiary'} roleType={'button'} size={'large'} onClick={handleRefreshQuote}>
                {i18n.getMessage('createTransaction.exchange.quote.refresh')}
              </Button>
            )}
          </div>
        )}

        {quote && (
          <div className={'exchange-transaction-rate-commission-wrapper'}>
            <div className={'exchange-transaction-rate-commission-block'}>
              <span className={'exchange-transaction-rate-commission-title'}>
                {i18n.getMessage('createTransaction.exchange.rate.label')}
              </span>
              <span className={'exchange-transaction-rate-commission-value'}>
                {`1 ${values.walletFrom?.currency || ''} = ${quote.rateDetails?.displayRate || quote.rate} ${
                  values.walletTo?.currency || ''
                }`}
              </span>
            </div>
            <div className={'exchange-transaction-rate-commission-block'}>
              <span className={'exchange-transaction-rate-commission-title'}>
                {i18n.getMessage('createTransaction.payment.commission.label')}
              </span>
              {calculation && (
                <span className={'exchange-transaction-rate-commission-value'}>
                  {calculation.commission} {calculation.commissionCurrency}
                </span>
              )}
            </div>
          </div>
        )}

        {quote && (
          <div className={'exchange-transaction-timer-wrapper'}>
            <span className={'exchange-transaction-timer-description'}>
              {i18n.getMessage('createTransaction.exchange.timer.description')}
            </span>
            <div className={'exchange-transaction-timer'}>
              <CountdownCircleTimer
                key={quote.quoteId}
                isPlaying
                duration={quote.duration}
                colors={[
                  timerColors.activeDefaultColor,
                  timerColors.activeDefaultColor,
                  timerColors.errorColor,
                  timerColors.errorColor
                ]}
                colorsTime={[quote.duration, Math.round(quote.duration / 3), Math.round(quote.duration / 6), 0]}
                onComplete={handleAutoRefreshQuote}
                size={60}
                strokeWidth={4}
              >
                {({ remainingTime }) => (
                  <div className={'exchange-transaction-timer-content'}>
                    <span className={'exchange-transaction-timer-value'}>{remainingTime}</span>
                  </div>
                )}
              </CountdownCircleTimer>
            </div>
          </div>
        )}

        <div className={'exchange-transaction-row-block'}>
          <AmountInput
            isRequired
            className={'exchange-transaction-input'}
            label={i18n.getMessage('createTransaction.exchange.fromAmount.label')}
            placeholder={i18n.getMessage('createTransaction.payment.amount.placeholder')}
            name={'fromAmount'}
            value={values.fromAmount}
            returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
            precision={getAmountPrecision(values.walletFrom)}
            onChange={handleFromAmountChange}
            isDisabled={!quote}
            error={errors?.fromAmount}
          />
          <Input
            isDisabled
            className={'exchange-transaction-select'}
            label={i18n.getMessage('createTransaction.payment.currency')}
            value={values.walletFrom?.currency || ''}
          />
          <AmountInput
            className={'exchange-transaction-input'}
            label={i18n.getMessage('createTransaction.exchange.toAmount.label')}
            placeholder={i18n.getMessage('createTransaction.payment.amount.placeholder')}
            name={'toAmount'}
            value={values.toAmount}
            returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
            precision={getAmountPrecision(values.walletTo)}
            onChange={handleToAmountChange}
            isDisabled={!quote}
          />
          <Input
            isDisabled
            className={'exchange-transaction-select'}
            label={i18n.getMessage('createTransaction.payment.currency')}
            value={values.walletTo?.currency || ''}
          />
        </div>

        {calculation?.amountExceeded && (
          <div className={'exchange-transaction-row-block'}>
            <Alert type={'warning'} message={i18n.getMessage('createTransaction.exchange.amountExceeded')} />
          </div>
        )}

        <div className={'exchange-transaction-submit-button-wrapper'}>
          <Button
            className={'exchange-transaction-submit-button'}
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            onClick={() => {}}
            isDisabled={!canSubmit}
          >
            {exchangeTransactionsStore.isLoading ? (
              <Loader />
            ) : (
              i18n.getMessage('createTransaction.exchange.button.createTransaction')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

ExchangeTransaction.propTypes = {
  customersStore: MobXPropTypes.observableObject,
  exchangeTransactionsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  customersStore: stores.customersStore,
  exchangeTransactionsStore: stores.exchangeTransactionsStore
}))(observer(ExchangeTransaction));
